import { useMemo, useState } from 'react'

type PillarId = 'capture' | 'protect' | 'index' | 'recall' | 'evolve'

type Pillar = {
  id: PillarId
  title: string
  tagline: string
  detail: string
  bullets: string[]
}

const pillars: Pillar[] = [
  {
    id: 'capture',
    title: 'Capture',
    tagline: 'Save what matters from conversations',
    detail:
      'We turn messy chats into small, reusable memories: what you want, what you avoid, what you are building, and what worked before.',
    bullets: ['Key takeaways', 'Source + time', 'Personal context'],
  },
  {
    id: 'protect',
    title: 'Protect',
    tagline: 'Private by default',
    detail:
      'Memories are encrypted. You choose what to share and when. Nothing is collected in the background without your intent.',
    bullets: ['Encrypted by default', 'You control sharing', 'No silent tracking'],
  },
  {
    id: 'index',
    title: 'Index',
    tagline: 'Find the right memory fast',
    detail:
      'Search brings back only the most relevant memories, so answers stay short, consistent, and useful.',
    bullets: ['Smart search', 'Simple filters', 'Fast recall'],
  },
  {
    id: 'recall',
    title: 'Recall',
    tagline: 'Answer from memory, not guesses',
    detail:
      'The agent relies on retrieved memories. If it cannot find enough context, it asks a clear question instead of making things up.',
    bullets: ['Memory-first answers', 'Fewer wrong assumptions', 'Ask when unsure'],
  },
  {
    id: 'evolve',
    title: 'Evolve',
    tagline: 'Stay accurate over time',
    detail:
      'People change. You can update, merge, or remove memories as needed and keep them consistent across sessions and devices.',
    bullets: ['Safe edits', 'Merge duplicates', 'Reviewable history'],
  },
]

export default function PersonalityMemory() {
  const [active, setActive] = useState<PillarId>('capture')
  const pillar = useMemo(() => pillars.find((p) => p.id === active) ?? pillars[0], [active])

  return (
    <section id="personality-memory" className="bg-bg">
      <div className="container-x py-14 md:py-18">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Personality Memory</h2>
            <p className="mt-2 max-w-3xl text-white/65">
              Soulon treats “persona” as a reliable memory system: clear takeaways, privacy controls, grounded recall, and safe updates over time.
            </p>
          </div>
          <a
            className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm font-semibold text-white/85 transition hover:bg-white/10 hover:text-white"
            href="/#how"
          >
            See the settlement flow
          </a>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="glass p-4">
              <div className="grid gap-2">
                {pillars.map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setActive(p.id)}
                    className={[
                      'w-full rounded-xl border px-4 py-3 text-left transition',
                      p.id === active
                        ? 'border-primary/40 bg-white/5'
                        : 'border-white/10 bg-transparent hover:bg-white/5',
                    ].join(' ')}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={[
                          'flex h-7 w-7 items-center justify-center rounded-lg text-xs font-semibold',
                          p.id === active ? 'bg-primary text-black' : 'bg-white/5 text-white/70',
                        ].join(' ')}
                      >
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-white/90">{p.title}</div>
                        <div className="truncate text-xs text-white/50">{p.tagline}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="glass2 p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-white/90">{pillar.title}</div>
                  <div className="mt-1 text-xs text-white/55">{pillar.tagline}</div>
                </div>
                <span className="rounded-full bg-glass-gradient px-3 py-1 text-xs font-semibold text-white/80">
                  Pillar {pillars.findIndex((p) => p.id === pillar.id) + 1}
                </span>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-white/70">{pillar.detail}</p>

              <div className="mt-6 grid gap-2 md:grid-cols-3">
                {pillar.bullets.map((b) => (
                  <div key={b} className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/70">
                    {b}
                  </div>
                ))}
              </div>

              <div className="mt-7 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-xs text-white/55">What this enables</div>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <EnableItem title="Consistent tone" desc="The agent stays aligned with your persona over time." />
                  <EnableItem title="Fewer repeats" desc="Less re-explaining context across sessions and devices." />
                  <EnableItem title="Safer automation" desc="Guardrails reduce risky actions and overreach." />
                  <EnableItem title="Grounded answers" desc="Responses stay aligned with what the system actually remembers." />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function EnableItem(props: { title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="text-xs font-semibold text-white/85">{props.title}</div>
      <div className="mt-1 text-xs text-white/55">{props.desc}</div>
    </div>
  )
}
